import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { isEmpty } from 'lodash-es';
import { parseQueryValue } from '@utils';
import {
  togglePinnedFiltersState,
  selectSellRequestsPinnedFilters,
} from '@store';
import { useHasFilters } from 'hooks/common';

export const useSellRequestsFilterConfig = ({ assignees, enums }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const pinnedFilters = useSelector(selectSellRequestsPinnedFilters);

  const handlePinnedFilterSelect = (pinnedFilter) => {
    dispatch(togglePinnedFiltersState({ board: 'sellRequests', pinnedFilter }));
  };

  const { hasFilters } = useHasFilters({
    pinnedFilters,
    omitParams: [
      'page',
      'view',
      'pageSize',
      'order',
      'orderBy',
      'query',
      'viewMode',
    ],
  });

  const filterCategories = useMemo(() => {
    const response = enums?.data?.data;
    const categories = [
      {
        id: 'assignee',
        label: 'Assignee',
        icon: PersonOutlineIcon,
        options: assignees || [],
        multiSelect: false,
      },
    ];

    if (
      response &&
      Array.isArray(response.categories) &&
      response.categories.length > 0
    ) {
      const enumCategories = response.categories.map((category) => {
        return {
          id: category.id,
          label: category.name,
          options: category.options,
          multiSelect: category.multiselect,
          icon: RadioButtonUncheckedIcon,
        };
      });
      categories.push(...enumCategories);
    }

    return categories;
  }, [enums?.data?.data, assignees]);

  const currentFilters = useMemo(() => {
    const response = enums?.data?.data;

    const filters = {};

    if (isEmpty(router.query)) {
      filters.assignee = '';
      return filters;
    }

    const assigneeValue = parseQueryValue(router.query.assignee);
    filters.assignee = assigneeValue || '';

    if (!response || response?.categories?.length === 0) {
      return filters;
    }

    response.categories.forEach((category) => {
      const value = parseQueryValue(router.query[category.id]);

      if (isEmpty(value)) {
        return;
      }

      filters[category.id] = value;
    });

    return filters;
  }, [router.query, enums?.data?.data]);

  const filterConfig = {
    filterCategories,
    currentFilters,
    pinnedFilters,
    handlePinnedFilterSelect,
    supportPinnedFilters: true,
    hasFilters,
  };

  return {
    filterConfig:
      filterConfig?.filterCategories?.length > 0 ? filterConfig : null,
  };
};

export default useSellRequestsFilterConfig;
